/**
 * Vesperas Stimme über die App spielen statt über WebKit. (17.10.2026, Seitenteil zu PawbotStimme.swift)
 *
 * ## Warum
 *
 * Hält die App das Mikro beim Vorlesen offen (Kippe `mikroBeimVorlesen`, Wach-Wort lauscht), gibt es zwei Besitzer der
 * Tonsitzung: den Mikrofon-Motor der App (`PlayAndRecord`) und WebKit, das `<audio>`/Web Audio in seinem Prozess
 * spielt. Jede Aktivierung der einen unterbricht die andere — Journal 16./17.10.: 412 × `route unterbrochen`, 163 ×
 * `tts-weiter`, nur beim Konto mit Lauscher (scratchpad/ios-vorlesen-plan.md). Ab dem App-Build mit `PawbotStimme`
 * spielt darum die App selbst: die Seite reicht rohes PCM (s16le) oder die Datei (MP3) über die Brücke, und WebKit
 * spielt in diesem Fall KEINEN Ton (tts-queue.ts `spieleUeberApp`).
 *
 * ## Schnittstelle (Plugin „PawbotVoice“, NICHT „PawbotRecorder“ — vgl. Setter-Fehler pre.3866)
 *
 *  - `stimmeBereit()` → `{bereit, grund, version, modus}` — je Antwort einmal gefragt. Ab `version: 2` (Build 115) gibt
 *    es zwei Modi (`sitzung:motor` = ein Abnehmer hält den Mikro-Motor, `sitzung:eigen` = die Stimme nimmt eine eigene
 *    Sitzung ohne Eingang) und die Gründe `kippe-aus`/`kein-lauscher` entfallen: die Seite prüft die Kippe NICHT mehr
 *    vorab — sie steuert nur noch, ob das Wach-Wort beim Vorlesen lauscht. Ohne `version` oder < 2 (Build ≤ 114) bleibt
 *    die alte Vorprüfung (Kippe AUS → Web).
 *  - `stimmeStueck({id, art, rate?, verstaerkungDb?, hoehenDb?, hoehenHz?, typ?, ab?})` → `{ok}` | `{ok:false, grund}`.
 *    `ok:false` heißt: dieses Stück spielt die Seite selbst (heutiger Web-Weg), auch `sitzung:<code>` (−1 Anruf/Telefon,
 *    −2 inzwischen ein Abnehmer da). `motor-steht:*` gibt es nur im Motor-Modus.
 *  - `stimmeDaten({id, daten, ende?})` — base64, je Aufruf ≤ 3 MB roh, `ende:true` zuletzt.
 *  - `stimmeStopp({id?})`, `stimmePause({an})`.
 *  - Ereignis `stimme` `{id, art, gespieltMs, grund}`; je Stück GENAU EINES von `ende`/`fehler`/`abbruch` — das ist
 *    das Ende des Stücks. `spur` (id leer) ist eine Protokollzeile der App. `unterbrochen` (grund `fremd`, eigene
 *    Sitzung: Siri/Telefon) → danach `weiter` oder `fehler unterbrochen`; bei dem Fehler setzt die Seite NICHT über
 *    WebKit fort (`NativErgebnis.fremdUnterbrochen`).
 *
 * Fehlt die Methode in der installierten App (`nativesPluginKennt`, Build ≤ 112), im Browser oder bei Kippe AUS:
 * nichts von hier, alles wie bisher.
 */
import { getCapacitorPlugin, nativesPluginKennt } from "./native-voice.js";
import { mikroBeimVorlesenAn } from "./mikro-beim-vorlesen.js";
import { meldeSpur } from "./mikro-spur.js";
import { _gesteWacheZuruecksetzen, nativBeginnt, nativEndet } from "./geste-wache.js";
import { alsBase64 } from "./vorlesen-nativ.js";

/** Ein Ereignis `stimme` der App, gelesen. */
export interface StimmeEreignis {
  id: string;
  art: string;
  gespieltMs: number;
  grund: string;
}

interface StimmePlugin {
  stimmeBereit(): Promise<{ bereit?: unknown; grund?: unknown; version?: unknown; modus?: unknown }>;
  stimmeStueck(o: {
    id: string;
    art: "pcm" | "datei";
    rate?: number;
    verstaerkungDb?: number;
    hoehenDb?: number;
    hoehenHz?: number;
    typ?: string;
    ab?: number;
  }): Promise<{ ok?: unknown; grund?: unknown }>;
  stimmeDaten(o: { id: string; daten: string; ende?: boolean }): Promise<{ ok?: unknown; grund?: unknown }>;
  stimmeStopp(o?: { id?: string }): Promise<unknown>;
  stimmePause(o: { an: boolean }): Promise<unknown>;
  /**
   * ⚠️ In der echten App (Seite über `server.url`, kein gebündeltes @capacitor/core, kein `registerPlugin`) kommt das
   *    alte `Capacitor.Plugins.PawbotVoice` — dessen `addListener` gibt `{remove}` SYNCHRON zurück, kein Promise.
   *    Darum nur mit `await` benutzen, nie `.then` (17.10.2026: „.then is not a function“ → Stimme stumm).
   */
  addListener(ereignis: "stimme", cb: (d: unknown) => void): Promise<{ remove(): void }> | { remove(): void };
}

/** So viel geht roh in EINEN Aufruf von `stimmeDaten` (die App nimmt bis 4 MB; base64 macht daraus 4/3). */
export const TEIL_BYTES = 3_000_000;
/** So groß darf eine Datei insgesamt sein (`StimmeRegel.MAX_DATEI_BYTES`); darüber spielt die Seite. */
export const MAX_DATEI_BYTES = 20_000_000;
/** Zuschlag des Wachhunds auf die erwartete Dauer (wie tts-queue.ts `WACHHUND_NACHLAUF_MS`). */
export const WACHHUND_ZUSCHLAG_MS = 10_000;
/** 17.10.2026: steht der Mikrofon-Motor gerade (`motor-steht…`), so oft noch einmal fragen … */
export const NATIV_NEU_FRAGEN_MS = 500;
/** … höchstens so lange ab der ersten Frage — danach spielt die Antwort im Web. */
export const NATIV_FRAGEN_MAX_MS = 1_500;
/** 27.10.2026: so lange darf die Frage „Motor wieder da?“ vor einem Stück dauern (nach einem Nein), sonst Web. */
export const NATIV_ZURUECK_FRIST_MS = 300;
/** 27.10.2026: so oft je Antwort zurück zur App — sagt sie „bereit“, lehnt aber jedes Stück ab, kein Hin und Her ohne Ende. */
export const NATIV_ZURUECK_MAX = 2;
/** Eine gleichlautende `spur`-Zeile der App höchstens so oft ins Journal (wie die Drossel des Wach-Worts). */
export const SPUR_ABSTAND_MS = 30_000;

function plugin(): StimmePlugin | null {
  if (!nativesPluginKennt("PawbotVoice", "stimmeStueck")) return null;
  return getCapacitorPlugin<StimmePlugin>("PawbotVoice");
}

/**
 * Kommt der native Weg überhaupt in Frage? In der App, die App kennt `stimmeStueck` (auf PawbotVoice), und die Kippe
 * „Mikro beim Vorlesen“ ist AN. Synchron — die Schlange fragt es beim Einreihen (keine Vorladung von `<audio>`, kein
 * Hi-Fi-Vorwärmen, rohe Bytes mitschreiben). Ob die App JETZT kann, sagt erst `stimmeNativBereit`.
 */
export function stimmeNativMoeglich(): boolean {
  if (!nativesPluginKennt("PawbotVoice", "stimmeStueck")) return false;
  // Ab Schnittstelle 2 entscheidet die Kippe nicht mehr. Solange die Version unbekannt ist (vor der ersten Antwort der
  // App), gilt die Annahme „neu“: `stimmeNativBereit` stellt die Antwort bei einer alten App auf Web um.
  if (appVersion >= 2 || appVersion === 0) return true;
  return mikroBeimVorlesenAn();
}

/** Die Schnittstellen-Version der App, aus der letzten Antwort von `stimmeBereit`; 0 = noch nicht gefragt. */
let appVersion = 0;

/** Ein Fehler als ein Wort (für Protokollzeilen). */
function kurz(e: unknown): string {
  const t = e instanceof Error ? e.name || e.message : String(e);
  return t.replace(/\s+/g, "-").slice(0, 40) || "unbekannt";
}

/** Ein Wort aus der App fürs Journal: nur Buchstaben, Ziffern, `:` `-` `_`. */
export function wort(v: unknown, fallback: string): string {
  return typeof v === "string" && v ? v.replace(/[^\w:-]/g, "").slice(0, 40) || fallback : fallback;
}

/**
 * Kann die App die Stimme jetzt spielen? Wirft nie; ohne Weg: `{bereit:false}` mit Grund. `modus` und `version` für das
 * Journal (`sitzung:motor` | `sitzung:eigen` | `-`; Version 1 = alte App ohne Angabe).
 */
export async function stimmeNativBereit(): Promise<{ bereit: boolean; grund: string; modus: string; version: number }> {
  const aus = (grund: string, modus = "-", version = appVersion || 1) => ({ bereit: false, grund, modus, version });
  try {
    const p = plugin();
    if (!p) return aus("methode-fehlt");
    const r = await p.stimmeBereit();
    const v = Number(r?.version);
    const version = Number.isFinite(v) && v >= 2 ? Math.floor(v) : 1;
    appVersion = version;
    const modus = wort(r?.modus, "-");
    // Alte App (Build ≤ 114): die Kippe „Mikro beim Vorlesen“ entscheidet vorab, wie bisher.
    if (version < 2 && !mikroBeimVorlesenAn()) return aus("kippe-aus-seite", modus, version);
    return { bereit: r?.bereit === true, grund: typeof r?.grund === "string" ? r.grund : "unbekannt", modus, version };
  } catch (e) {
    return aus(`aufruf:${kurz(e)}`);
  }
}

/** Die Antwort der App auf `stimmeBereit`. */
export type NativBereit = Awaited<ReturnType<typeof stimmeNativBereit>>;

/** `ms` warten; ein Abbruch beendet das Warten sofort. */
function schlafe(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((fertig) => {
    const ende = (): void => {
      clearTimeout(uhr);
      signal.removeEventListener("abort", ende);
      fertig();
    };
    const uhr = setTimeout(ende, ms);
    signal.addEventListener("abort", ende, { once: true });
  });
}

/**
 * Die erste Frage einer Antwort (V8 tts-queue.ts `nativEntscheiden`, V9 neun-stimme.ts): `stimmeBereit`, und steht der Motor
 * (`motor-steht…`), alle `NATIV_NEU_FRAGEN_MS` neu, höchstens `maxMs` ab der ersten Frage (0 = einmal). Der Motor steht etwa, solange die Seite
 * ein Mikrofon hält oder es gerade erst freigab (`motor-steht:sprechtaste`: der Lauscher der App läuft erst kurz danach wieder an), oder im
 * Hintergrund. `beiVersuch` bekommt jede Antwort mit Versuch und ms seit der ersten Frage (Journal). Ein Abbruch beendet das Warten. Wirft nie.
 */
export async function stimmeNativBereitWarten(
  signal: AbortSignal,
  maxMs: number,
  beiVersuch?: (r: NativBereit, versuch: number, nachMs: number) => void,
): Promise<NativBereit> {
  const t0 = Date.now();
  for (let versuch = 1; ; versuch++) {
    const r = await stimmeNativBereit();
    const nach = Date.now() - t0;
    beiVersuch?.(r, versuch, nach);
    const warten = Math.min(NATIV_NEU_FRAGEN_MS, maxMs - nach);
    if (r.bereit || !r.grund.startsWith("motor-steht") || warten <= 0 || signal.aborted) return r;
    await schlafe(warten, signal);
    if (signal.aborted) return r;
  }
}

/** Nach einem Nein wegen des Motors: noch einmal fragen, höchstens `NATIV_ZURUECK_FRIST_MS` (die Brücke kann hängen); null = keine Antwort. */
export function stimmeNativBereitKurz(signal: AbortSignal): Promise<NativBereit | null> {
  return Promise.race([stimmeNativBereit(), schlafe(NATIV_ZURUECK_FRIST_MS, signal).then(() => null)]);
}

// ---------- Ereignisse ----------

const wartende = new Map<string, (e: StimmeEreignis) => void>();
const pauseHoerer = new Set<() => void>();
let hoerer: Promise<boolean> | null = null;
/** Hat die App selbst angehalten (Ereignis `pause`, Kopfhörer weg)? Bis die Seite wieder fortsetzt. */
let appPausiert = false;
const spurZuletzt = new Map<string, number>();

function lies(d: unknown): StimmeEreignis {
  const o = (d ?? {}) as Record<string, unknown>;
  const ms = Number(o.gespieltMs);
  return {
    id: typeof o.id === "string" ? o.id : "",
    art: typeof o.art === "string" ? o.art : "",
    gespieltMs: Number.isFinite(ms) && ms > 0 ? Math.round(ms) : 0,
    grund: typeof o.grund === "string" ? o.grund.slice(0, 120) : "",
  };
}

/** `spur`-Zeilen der App ins Journal — gleichlautende höchstens alle `SPUR_ABSTAND_MS`. */
function spur(zeile: string, jetzt = Date.now()): void {
  const z = zeile.trim();
  if (!z) return;
  const vorher = spurZuletzt.get(z);
  if (vorher !== undefined && jetzt - vorher < SPUR_ABSTAND_MS) return;
  spurZuletzt.set(z, jetzt);
  if (spurZuletzt.size > 200) spurZuletzt.clear();
  meldeSpur("stimme-nativ", `spur ${z}`);
}

function verteile(e: StimmeEreignis): void {
  if (e.art === "spur") {
    spur(e.grund);
    return;
  }
  if (e.art === "pause") {
    appPausiert = true;
    stimmeNativPauseMelden();
  }
  wartende.get(e.id)?.(e);
}

/**
 * Den Hörer für `stimme` einmal anmelden. `true` = angemeldet; `false` = ging nicht (dann spielt die Seite, nie stumm).
 * Wirft nie — auch nicht, wenn `addListener` synchron wirft oder kein Promise liefert (alte Hülle, s. `StimmePlugin`).
 */
function hoererBereit(p: StimmePlugin): Promise<boolean> {
  hoerer ??= (async () => {
    await p.addListener("stimme", (d) => verteile(lies(d)));
    return true;
  })().catch(() => {
    hoerer = null;
    return false;
  });
  return hoerer;
}

/**
 * Die App hat die Stimme selbst angehalten (Kopfhörer/Auto getrennt): wer die Anzeige führt, gleicht sie an
 * (app.ts → Pause wie vom Sperrbildschirm). Gibt einen Abmelder zurück.
 */
export function aufStimmeNativPauseHoeren(cb: () => void): () => void {
  pauseHoerer.add(cb);
  return () => pauseHoerer.delete(cb);
}

/** Wer die Anzeige führt, erfährt: die Stimme der App steht (Pause von der App oder ihre Pausenfrist). */
export function stimmeNativPauseMelden(): void {
  for (const cb of pauseHoerer) {
    try {
      cb();
    } catch {
      /* ein Hörer darf die Stimme nie stören */
    }
  }
}

/**
 * Beim Start der Seite: was eine frühere Seite (vor dem Neuladen) der App noch übergeben hatte, beenden — diese Seite
 * weiß von keinem Stück (wie `vorleseHaltZuruecksetzen`). Ohne die Methode (alte App, Browser) nichts. Wirft nie.
 */
export function stimmeNativZuruecksetzen(): void {
  const p = plugin();
  if (!p) return;
  try {
    void p.stimmeStopp({}).catch(() => undefined);
  } catch {
    /* egal */
  }
}

/** Pause/Fortsetzen der Seite an die App. Wirft nie. */
export function stimmeNativPause(an: boolean): void {
  appPausiert = an;
  const p = plugin();
  if (!p) return;
  try {
    void p.stimmePause({ an }).catch(() => undefined);
  } catch {
    /* egal */
  }
}

// ---------- Ein Stück ----------

/** Woher die Bytes kommen: liefert, was gerade da ist; `ende` = danach kommt nichts mehr; `fehler` = Strom riss. */
export interface NativQuelle {
  naechste(): Promise<{ bytes: Uint8Array[]; ende: boolean; fehler?: string }>;
}

/** Eine Datei, die schon ganz da ist. */
export function dateiQuelle(bytes: Uint8Array): NativQuelle {
  let gegeben = false;
  return {
    naechste: () => {
      const b = gegeben ? [] : [bytes];
      gegeben = true;
      return Promise.resolve({ bytes: b, ende: true });
    },
  };
}

/** Ein PCM-Stück der Seite (tts-pcm.ts `PcmStueck`, mit `rohMerken`): die rohen Brocken der Reihe nach. */
export function pcmQuelle(pcm: {
  rohAb(ab: number): Uint8Array[];
  rohLage(): { fertig: boolean; fehler: string | null };
  rohWarten(maxMs: number): Promise<void>;
}): NativQuelle {
  let ab = 0;
  return {
    naechste: async () => {
      for (;;) {
        const neu = pcm.rohAb(ab);
        const lage = pcm.rohLage();
        ab += neu.length;
        if (neu.length > 0 || lage.fertig || lage.fehler !== null) {
          return { bytes: neu, ende: lage.fertig || lage.fehler !== null, ...(lage.fehler !== null ? { fehler: lage.fehler } : {}) };
        }
        await pcm.rohWarten(1_000);
      }
    },
  };
}

/**
 * Bis das erste PCM-Paket eines Stücks da ist (Kopfzeile und rohe Bytes), der Strom endet/scheitert, `signal` abbricht oder `fristMs` um sind.
 * Erst dann darf `stimmeStueck` kommen (die App gibt einem schweigenden Strom nur 8 s). Für V8 (tts-queue.ts) und V9 (neun-stimme.ts).
 */
export async function erstesPaket<L extends { brocken: number; fertig: boolean; fehler: string | null }>(
  pcm: { start(): void; rohLage(): L; rohWarten(maxMs: number): Promise<void> },
  signal: AbortSignal,
  fristMs: number,
): Promise<L> {
  pcm.start();
  const bis = Date.now() + fristMs;
  for (;;) {
    const l = pcm.rohLage();
    if (l.brocken > 0 || l.fertig || l.fehler !== null || signal.aborted || Date.now() >= bis) return l;
    await pcm.rohWarten(Math.min(250, Math.max(1, bis - Date.now())));
  }
}

/** Bytes in Teile ≤ `max` — kleine Brocken zusammen, große geteilt. */
export function teile(brocken: Uint8Array[], max = TEIL_BYTES): Uint8Array[] {
  const aus: Uint8Array[] = [];
  let sammel: Uint8Array[] = [];
  let n = 0;
  const abgeben = (): void => {
    if (n === 0) return;
    const t = new Uint8Array(n);
    let o = 0;
    for (const s of sammel) {
      t.set(s, o);
      o += s.length;
    }
    aus.push(t);
    sammel = [];
    n = 0;
  };
  for (const b of brocken) {
    for (let i = 0; i < b.length; i += max) {
      const s = b.subarray(i, Math.min(b.length, i + max));
      if (n + s.length > max) abgeben();
      sammel.push(s);
      n += s.length;
    }
  }
  abgeben();
  return aus;
}

export type NativErgebnis =
  | { art: "abgelehnt"; grund: string }
  | {
      art: "ende" | "fehler" | "abbruch";
      id: string;
      grund: string;
      gespieltMs: number;
      /** Kam das Ereignis `start` (erster Ton)? */
      gestartet: boolean;
      /** Riss die Quelle (Strom), bevor alles da war? Dann spielte die App nur, was kam. */
      quelleFehler: string | null;
      /** Meldete die App `unterbrochen` mit Grund `fremd` (Siri, Anruf)? Endet das Stück im Fehler, setzt die Seite nicht fort. */
      fremdUnterbrochen: boolean;
    };

let idZaehler = 0;
function neueId(): string {
  idZaehler += 1;
  return `s${Date.now().toString(36)}-${idZaehler}`;
}

/**
 * Ein Stück über die App spielen. Löst mit dem endgültigen Ereignis seiner `id` (oder `abgelehnt`: dann spielt die
 * Seite dieses Stück selbst, es wurde nichts gesendet). Wirft nie.
 *
 * ⚠️ `stimmeStueck` kommt erst, wenn die Quelle etwas hat (die App gibt einem schweigenden Strom 8 s) — das wartet
 *    der Aufrufer ab (tts-queue.ts). Die Daten gehen der Reihe nach (jeder Aufruf wird abgewartet).
 * ⚠️ Abbruch der Seite (`signal`): sofort `stimmeStopp({id})` und `abbruch` — nicht auf die App warten.
 * ⚠️ Wachhund: kommt nach dem letzten Teil in `deckelMs` (nur nicht pausierte Zeit, jedes Ereignis setzt zurück)
 *    kein endgültiges Ereignis, endet das Stück als `fehler wachhund` — eine verlorene Brücke hält die Schlange nie an.
 */
export async function stimmeNativSpielen(o: {
  art: "pcm" | "datei";
  kopf: { rate?: number; verstaerkungDb?: number; hoehenDb?: number; hoehenHz?: number; typ?: string; ab?: number };
  quelle: NativQuelle;
  signal: AbortSignal;
  /** Hat die Seite pausiert? (für den Wachhund) */
  pausiert: () => boolean;
  /** Die App hat das Stück angenommen (vor dem ersten Ton). */
  beiAnnahme?: () => void;
  /** Erster Ton (Ereignis `start`). */
  beiStart?: () => void;
  /** Jedes Ereignis dieses Stücks (für die gespielte Stelle). */
  beiEreignis?: (e: StimmeEreignis) => void;
  /** Wachhund ab dem letzten Teil, aus den gesendeten Bytes. */
  deckelMs: (bytes: number) => number;
}): Promise<NativErgebnis> {
  const p = plugin();
  if (!p) return { art: "abgelehnt", grund: "methode-fehlt" };
  if (!(await hoererBereit(p))) return { art: "abgelehnt", grund: "kein-hoerer" };
  const id = neueId();
  let gespieltMs = 0;
  let gestartet = false;
  let quelleFehler: string | null = null;
  let fremdUnterbrochen = false;
  let erledigt = false;
  /** Zählt dieses Stück als „native Stimme spielt“ (geste-wache.ts)? Von der Annahme bis zum Schluss. */
  let zaehlt = false;
  let aktivMs = 0;
  /** iOS hat unterbrochen (Ereignis `unterbrochen` bis `weiter`): die App setzt selbst fort — der Wachhund ruht. */
  let unterbrochen = false;
  let wache: ReturnType<typeof setInterval> | undefined;
  let ergebnis!: (e: NativErgebnis) => void;
  const fertig = new Promise<NativErgebnis>((r) => (ergebnis = r));
  const schluss = (art: "ende" | "fehler" | "abbruch", grund: string): void => {
    if (erledigt) return;
    erledigt = true;
    if (zaehlt) {
      zaehlt = false;
      nativEndet();
    }
    wartende.delete(id);
    o.signal.removeEventListener("abort", onAbort);
    if (wache !== undefined) clearInterval(wache);
    ergebnis({ art, id, grund, gespieltMs, gestartet, quelleFehler, fremdUnterbrochen });
  };
  const stopp = (): void => {
    try {
      void p.stimmeStopp({ id }).catch(() => undefined);
    } catch {
      /* egal */
    }
  };
  // Erst selbst beenden, dann der App sagen: ihr `abbruch` kommt danach und trifft kein Warten mehr.
  const onAbort = (): void => {
    schluss("abbruch", "seite");
    stopp();
  };
  wartende.set(id, (e) => {
    aktivMs = 0;
    if (e.art === "unterbrochen") {
      unterbrochen = true;
      if (e.grund === "fremd") fremdUnterbrochen = true;
    } else if (e.art === "weiter") {
      unterbrochen = false;
      fremdUnterbrochen = false; // iOS bot Fortsetzen an: ein späterer anderer Fehler ist wieder ein normaler Rückfall
    } else if (e.art === "start") unterbrochen = false;
    if (e.gespieltMs > gespieltMs) gespieltMs = e.gespieltMs;
    if (e.art === "start" && !gestartet) {
      gestartet = true;
      try {
        o.beiStart?.();
      } catch {
        /* eine Sonde darf die Stimme nie stören */
      }
    }
    try {
      o.beiEreignis?.(e);
    } catch {
      /* egal */
    }
    if (e.art === "ende" || e.art === "fehler" || e.art === "abbruch") schluss(e.art, e.grund);
  });
  let antwort: { ok?: unknown; grund?: unknown };
  try {
    antwort = await p.stimmeStueck({ id, art: o.art, ...o.kopf });
  } catch (e) {
    wartende.delete(id);
    return { art: "abgelehnt", grund: `aufruf:${kurz(e)}` };
  }
  if (antwort?.ok !== true) {
    wartende.delete(id);
    // Kam der Abbruch, während die App antwortete, ist das Stück für sie trotzdem weg.
    return { art: "abgelehnt", grund: typeof antwort?.grund === "string" && antwort.grund ? antwort.grund : "unbekannt" };
  }
  if (!erledigt) {
    zaehlt = true;
    nativBeginnt();
  }
  if (o.signal.aborted) {
    onAbort();
    return fertig;
  }
  o.signal.addEventListener("abort", onAbort, { once: true });
  try {
    o.beiAnnahme?.();
  } catch {
    /* ein Hörer darf die Stimme nie stören */
  }
  // Pausierte die Seite, während das Stück vorbereitet wurde: die App spielt nicht von selbst an.
  // Sonst gilt eine alte Pause der App (ein früheres Stück) für den Wachhund dieses Stücks nicht mehr.
  if (o.pausiert()) stimmeNativPause(true);
  else appPausiert = false;
  const wachen = (bytes: number): void => {
    if (erledigt || wache !== undefined) return;
    let zuletzt = Date.now();
    wache = setInterval(() => {
      const jetzt = Date.now();
      if (!o.pausiert() && !appPausiert && !unterbrochen) aktivMs += Math.min(jetzt - zuletzt, 1_500);
      zuletzt = jetzt;
      if (aktivMs >= o.deckelMs(bytes)) {
        schluss("fehler", "wachhund");
        stopp();
      }
    }, 500);
  };
  void (async () => {
    let bytes = 0;
    try {
      for (;;) {
        if (erledigt) return;
        const n = await o.quelle.naechste();
        for (const t of teile(n.bytes)) {
          if (erledigt) return;
          const a = await p.stimmeDaten({ id, daten: alsBase64(t), ende: false });
          bytes += t.length;
          if (a?.ok === false) {
            // Die App nimmt nichts mehr (zu groß, schon zu Ende): ihr Ereignis folgt — sonst der Wachhund.
            wachen(bytes);
            return;
          }
        }
        if (n.fehler) quelleFehler = n.fehler;
        if (n.ende) break;
      }
      if (erledigt) return;
      await p.stimmeDaten({ id, daten: "", ende: true });
      wachen(bytes);
    } catch (e) {
      schluss("fehler", `bruecke:${kurz(e)}`);
      stopp();
    }
  })();
  return fertig;
}

/** Nur für Tests: Hörer, Wartende und Drossel vergessen. */
export function _stimmeNativVergessen(): void {
  wartende.clear();
  pauseHoerer.clear();
  spurZuletzt.clear();
  hoerer = null;
  appPausiert = false;
  appVersion = 0;
  _gesteWacheZuruecksetzen();
}
